import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Client } from '@twilio/conversations';
import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';

import { useAppResume } from '@/hooks/useAppResume';
import { startInboxSync } from '@/services/chat/inboxSync';
import {
  TwilioChatTransport,
  twilioClientOf,
  twilioInboxEvents,
} from '@/services/chat/twilioTransport';
import { postInquiryMessage } from '@/services/chat/postInquiryMessage';
import { getTwilioTokenQueryOptionsV2 } from '@/services/getTwilioTokenV2';
import { actAsUserIdAtom, userIdAtom } from '@/services/globalState';
import {
  selectChatTransport,
  selectSetChatTransport,
  useChatTransportStore,
} from '@/services/stores/chatTransportStore';
import { trackChatSessionRecovered, type ChatSessionRecoverySource } from '@/util/analyticsEvents';
import { shouldFetchUserScoped } from '@/util/authTokens';
import { Sentry } from '@/util/sentry';
import { hasInjectedChatTransport } from '@/util/testOverrides';

/**
 * Attempts to recover a Twilio session with a fresh token before giving up and
 * reporting. Three covers the observed failure shape — a token that expired
 * while the tab was suspended — without hammering the token endpoint when the
 * grant itself is bad.
 */
const MAX_TOKEN_RECOVERY_ATTEMPTS = 3;

const TOKEN_RECOVERY_BASE_DELAY_MS = 1000;

/**
 * A resume gets its own budget because it is racing the radio rather than a bad
 * grant: the first attempt often lands before the network is usable, and the
 * ones after it cost a token request each.
 */
const MAX_RESUME_TOKEN_REFRESH_ATTEMPTS = 3;

/**
 * Twilio access-token error codes (20101-20107) cover an invalid, malformed,
 * mis-issued, or expired JWT. Every one of them is cleared by minting a new
 * token, so they are recoverable rather than reportable.
 *
 * https://www.twilio.com/docs/api/errors#2-anchor
 */
const TWILIO_TOKEN_ERROR_CODE_MIN = 20101;

const TWILIO_TOKEN_ERROR_CODE_MAX = 20107;

/**
 * True when a Twilio connection failure is one a fresh access token fixes.
 *
 * The SDK reports these as terminal — it will not retry on its own — so
 * without an explicit refresh the chat surfaces stay empty until the user
 * reloads the app.
 */
export function isRecoverableTokenError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const { errorCode, httpStatusCode } = error as {
    errorCode?: unknown;
    httpStatusCode?: unknown;
  };

  if (httpStatusCode === 401) {
    return true;
  }

  return (
    typeof errorCode === 'number' &&
    errorCode >= TWILIO_TOKEN_ERROR_CODE_MIN &&
    errorCode <= TWILIO_TOKEN_ERROR_CODE_MAX
  );
}

/**
 * The Sync layer's message for a request that died with the socket.
 *
 * Sync stamps every error it raises with its own status and code, defaulting
 * both to zero, so the trailing pair reads as "nothing answered" rather than as
 * an HTTP status. A real rejection arrives under its own name and a nonzero
 * pair — `Unauthorized (status: 401, code: 20104)` — which is why only the bare
 * drop and its zeroed form belong here.
 */
const SOCKET_DROP_MESSAGE = /^disconnected(?: \(status: 0, code: 0\))?$/;

/**
 * twilsock re-wraps the same drop when the socket goes during its own init
 * request: `Unknown error during connection initialization: <message>\n<cause>`.
 * That is the form the SDK hands `initFailed` in production. Either spelling is
 * accepted so an SDK wording change does not silently end recovery.
 */
const WRAPPED_DROP_PREFIX = /^Unknown error during connection initiali[sz]ation: /;

const STATUS_OR_CODE_KEY = /(?:status|code)$/i;

/**
 * True when the serialized cause says nothing answered: empty, `undefined`,
 * `null`, or a JSON object holding nothing but zeroed status and code fields. Anything
 * else, including text that is not JSON, may be a service answer and is not
 * treated as a drop.
 */
function isUnansweredCause(cause: string): boolean {
  const text = cause.trim();

  if (text === '' || text === 'undefined' || text === 'null') {
    return true;
  }

  try {
    const parsed: unknown = JSON.parse(text);

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return false;
    }

    return Object.entries(parsed).every(
      ([key, value]) => STATUS_OR_CODE_KEY.test(key) && Number(value) === 0
    );
  } catch {
    return false;
  }
}

function isSocketDropMessage(message: string): boolean {
  if (SOCKET_DROP_MESSAGE.test(message)) {
    return true;
  }

  const wrapper = WRAPPED_DROP_PREFIX.exec(message);

  if (!wrapper) {
    return false;
  }

  const [detail = '', ...cause] = message.slice(wrapper[0].length).split('\n');

  return SOCKET_DROP_MESSAGE.test(detail) && isUnansweredCause(cause.join('\n'));
}

/**
 * True when a terminal Twilio failure is the socket going away rather than the
 * service answering.
 *
 * The Conversations client runs its own initialization inside a one-shot
 * `once('connected')` handler and reports any throw from it as `initFailed`.
 * A socket that drops while that initialization is in flight therefore ends the
 * session outright: the handler has already been consumed, so nothing re-runs
 * initialization even after the transport reconnects underneath. Chat stays
 * empty for the rest of the session, and only a rebuild brings it back.
 *
 * Nothing about it is a defect — it is the network being gone at the moment
 * initialization ran — so it belongs in the same bounded recovery as an expired
 * token rather than in an error report.
 *
 * `errorCode` and `httpStatusCode` are defensive: the SDK reduces the cause to
 * `terminal` and `message`, so they should never survive to here. They stay
 * because the day one does, the service answered, and an answer we did not
 * expect is worth reporting. The wrapped form is the exception, since it
 * carries its serialized cause inside `message`, which `isUnansweredCause`
 * reads. The zeroes in the message are Sync's own `status` and `code` fields,
 * named differently from the two checked here.
 */
export function isTransientSocketFailure(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const { message, errorCode, httpStatusCode } = error as {
    message?: unknown;
    errorCode?: unknown;
    httpStatusCode?: unknown;
  };

  // Truthiness, not presence: a zero in either field is the SDK's way of saying
  // "no code", which is the case this is meant to accept rather than reject.
  if (errorCode || httpStatusCode) {
    return false;
  }

  return typeof message === 'string' && isSocketDropMessage(message);
}

/**
 * Twilio Conversations SDK integration hook.
 *
 * Manages Twilio client lifecycle, token fetching, and cleanup on user changes.
 * Cancels and removes any Twilio token queries regardless of previous user id during cleanup.
 * Removes listeners first to avoid triggering token refresh on disconnect.
 *
 * When actAsUserId changes (impersonation), the client is fully reinitialized:
 * 1. Existing client is shut down
 * 2. Token cache is cleared
 * 3. New token is fetched (with X-Act-As header for impersonated user)
 * 4. New client is created with the impersonated user's identity
 */
export const useTwilio = () => {
  const twilioClient = useChatTransportStore(selectChatTransport);

  const setTwilioClient = useChatTransportStore(selectSetChatTransport);

  const userId = useAtomValue(userIdAtom);

  const actAsUserId = useAtomValue(actAsUserIdAtom);

  const prevActAsUserIdRef = useRef<string | undefined>(actAsUserId);

  const tokenRecoveryAttemptsRef = useRef(0);

  const lastBuiltTokenRef = useRef<string | undefined>(undefined);

  const recoverySourceRef = useRef<ChatSessionRecoverySource | undefined>(undefined);

  const hasReportedRecoveryExhaustionRef = useRef(false);

  const teardownClientRef = useRef<(() => void) | undefined>(undefined);

  /**
   * Whether the client currently installed got through SDK initialization.
   *
   * This is what separates a session the SDK will reconnect on its own from one
   * that can only come back through a rebuild, so it is reset per client build
   * rather than per user.
   */
  const hasInitializedRef = useRef(false);

  /**
   * Whether the installed client ended in a terminal initialization failure.
   *
   * A client that has not initialized is not necessarily a client in trouble —
   * for the first seconds of every session it is simply still connecting, and
   * on iOS a resume arrives for every glance at control center. Only a client
   * the SDK has actually given up on may be rebuilt, so this records that the
   * SDK said so rather than inferring it from the absence of success.
   */
  const hasInitFailedRef = useRef(false);

  /**
   * The client currently installed, including one that never initialized.
   *
   * The atom only ever holds a client that came up, so a failed one is
   * unreachable through it — and a failed one is exactly what the resume
   * handler needs to find.
   */
  const clientRef = useRef<Client | undefined>(undefined);

  const foregroundRefreshInFlightRef = useRef(false);

  const recoveryInFlightRef = useRef(false);

  const { refetch, data } = useQuery({
    ...getTwilioTokenQueryOptionsV2(userId),
    // When a test injects a fake transport, the real token/client are bypassed.
    enabled: shouldFetchUserScoped(userId) && !hasInjectedChatTransport(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const queryClient = useQueryClient();

  /**
   * Shut down the installed client and drop what `setupClient` left running.
   *
   * A client whose initialization failed never reaches the atom, so nothing
   * else holds a reference that could shut it down, and the debounced
   * invalidation timers outlive it either way. Recovery rebuilds up to three
   * times per drop, so without this every outage strands another client and
   * another set of timers.
   */
  const teardownClient = () => {
    teardownClientRef.current?.();

    teardownClientRef.current = undefined;
  };

  const reinitializeTwilioClient = () => {
    if (hasInjectedChatTransport()) {
      return;
    }

    if (!twilioClient) {
      return;
    }

    teardownClient();

    try {
      (
        twilioClientOf(twilioClient) as unknown as { removeAllListeners?: () => void } | undefined
      )?.removeAllListeners?.();
    } catch {}

    // The teardown above may already have shut this client down. Twilio makes
    // no idempotency promise about `shutdown`, and a throw here would escape
    // into the effect that called it.
    try {
      twilioClient.shutdown();
    } catch {}

    setTwilioClient(undefined);

    queryClient.removeQueries({
      predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'twilioTokenV2',
    });

    if (shouldFetchUserScoped(userId)) {
      refetch();
    }
  };

  /**
   * Set once the session unmounts. A token fetch or `updateToken` that settles
   * after the mode switches away would otherwise build a client that writes
   * the transport store over the session now running.
   */
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    isUnmountedRef.current = false;

    return () => {
      isUnmountedRef.current = true;

      teardownClient();
    };
  }, []);

  useEffect(() => {
    if (prevActAsUserIdRef.current !== actAsUserId) {
      prevActAsUserIdRef.current = actAsUserId;

      reinitializeTwilioClient();
    }
  }, [actAsUserId, twilioClient, userId, queryClient, refetch, setTwilioClient]);

  useEffect(() => {
    // Test seam: a fake transport was injected before the app booted. Install
    // it once and leave the real token/client path untouched. Dynamic import
    // keeps the fake out of the production bundle.
    if (hasInjectedChatTransport()) {
      let cancelled = false;

      import('@/services/chat/testChatTransport')
        .then(({ installTestChatTransport }) => {
          if (cancelled) {
            return;
          }

          const transport = installTestChatTransport();

          if (transport) {
            setTwilioClient(transport);
          }
        })
        .catch(() => undefined);

      return () => {
        cancelled = true;
      };
    }

    // A token this hook already built a client from means the recovery path
    // got there first; running `createClient` again would stand up a second
    // client against the same token.
    if (userId && data && data.token !== lastBuiltTokenRef.current) {
      createClient(data.token);

      return;
    }

    if (!userId) {
      teardownClient();

      try {
        (
          twilioClientOf(twilioClient) as unknown as { removeAllListeners?: () => void } | undefined
        )?.removeAllListeners?.();
      } catch {}

      try {
        twilioClient?.shutdown();
      } catch {}

      setTwilioClient(undefined);

      queryClient.cancelQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'twilioTokenV2',
      });

      queryClient.removeQueries({
        predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'twilioTokenV2',
      });
    }
  }, [userId, data]);

  const updateTwilioToken = async () => {
    if (!shouldFetchUserScoped(userId)) {
      return;
    }

    await refetch();
  };

  /**
   * Mint a fresh token after a resume, retrying a failed attempt.
   *
   * The SDK reconnects a suspended session on its own, but it reconnects with
   * the token it already has — and the timers that would have refreshed one do
   * not run while the app is suspended or the tab is asleep. So the token, not
   * the socket, is what a resume has to repair, and repairing it early is what
   * turns a rejected reconnect into no event at all.
   *
   * The retry is the point. A resume is the moment a device is least likely to
   * have a usable network, since the radio is often still coming up, and the
   * token query does not retry on its own. A single failed attempt would leave
   * an expired token in place until the connection was denied.
   *
   * Failure here is not reported. The denied path is still armed and recovers
   * from exactly this state, so a resume that cannot reach the network has
   * lost nothing except the head start.
   */
  const refreshTokenAfterResume = async () => {
    if (foregroundRefreshInFlightRef.current) {
      return;
    }

    foregroundRefreshInFlightRef.current = true;

    try {
      for (let attempt = 1; attempt <= MAX_RESUME_TOKEN_REFRESH_ATTEMPTS; attempt += 1) {
        if (!shouldFetchUserScoped(userId)) {
          return;
        }

        const { isError } = await refetch();

        if (!isError) {
          return;
        }

        if (attempt < MAX_RESUME_TOKEN_REFRESH_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, TOKEN_RECOVERY_BASE_DELAY_MS * attempt)
          );
        }
      }

      Sentry.addBreadcrumb({
        category: 'twilio',
        level: 'warning',
        message: 'Could not refresh the Twilio token after a resume',
      });
    } finally {
      foregroundRefreshInFlightRef.current = false;
    }
  };

  /**
   * Bring the chat session back when the app or tab returns.
   *
   * Twilio is explicit that a shutdown/create cycle is the wrong answer to a
   * foreground or a network drop, because a client that got through
   * initialization reconnects on its own. That guidance assumes there is a live
   * session to reconnect, and a client that failed during initialization does
   * not have one: the SDK's initialization handler is one-shot, so the
   * transport can come back underneath a client that will never finish coming
   * up. Rebuilding is reserved for exactly that case.
   *
   * For a session that did initialize, the only thing a resume can usefully do
   * is refresh the token, and only a foreground can have outlived one — a
   * network drop expires nothing.
   */
  useAppResume((reason) => {
    const client = clientRef.current;

    if (!client || hasInjectedChatTransport()) {
      return;
    }

    if (hasInitFailedRef.current) {
      // The bounded recovery spends its budget in seconds, so without this any
      // longer outage leaves chat empty for the rest of the session. The budget
      // resets first, because the condition that spent it is the one that just
      // ended.
      tokenRecoveryAttemptsRef.current = 0;

      hasReportedRecoveryExhaustionRef.current = false;

      recoverChatSession('networkDrop').catch(() => {});

      return;
    }

    if (!hasInitializedRef.current) {
      // Still connecting. The SDK is working, and a resume this early is far
      // more often a glance at control center than a problem.
      return;
    }

    if (reason === 'foreground' && client.connectionState !== 'connected') {
      refreshTokenAfterResume().catch(() => {});
    }
  });

  /**
   * Mint a fresh token and rebuild the session after a terminal failure.
   *
   * `initFailed` and `connectionState === 'denied'` are both terminal per the
   * SDK: nothing retries on its own, so chat stays empty until a reload. Two
   * causes reach here. An access token that expired while the tab or app was
   * suspended, which a new token clears; and the socket being gone at the
   * moment init ran, which a rebuild clears once the network is back. The
   * remedy is the same either way, so `source` exists to tell them apart
   * afterwards rather than to branch on.
   *
   * Reports only once the attempt budget is spent, so a recovered session
   * costs a breadcrumb rather than an error event.
   */
  const recoverChatSession = async (source: ChatSessionRecoverySource) => {
    if (!shouldFetchUserScoped(userId)) {
      return;
    }

    // A rebuild spans a backoff and a token request, and every trigger that
    // starts one can fire again while it is in flight — a flapping network on
    // web, a repeated foreground on native. Without this, each event starts
    // another rebuild that tears the previous one down mid-initialization, so
    // no client ever reaches `initialized`, the budget never resets, and the
    // bounded recovery becomes an unbounded loop.
    if (recoveryInFlightRef.current) {
      return;
    }

    if (tokenRecoveryAttemptsRef.current >= MAX_TOKEN_RECOVERY_ATTEMPTS) {
      // Twilio re-emits 'denied' on every reconnect attempt against a bad
      // grant, so without this guard one unusable token becomes a stream of
      // identical error events — the volume this whole change set out to stop.
      if (!hasReportedRecoveryExhaustionRef.current) {
        hasReportedRecoveryExhaustionRef.current = true;

        Sentry.captureException(new Error('Twilio session recovery exhausted'), {
          tags: { source: `twilio:${source}` },
          extra: { userId, attempts: tokenRecoveryAttemptsRef.current },
        });
      }

      return;
    }

    recoveryInFlightRef.current = true;

    try {
      tokenRecoveryAttemptsRef.current += 1;

      recoverySourceRef.current = source;

      const attempt = tokenRecoveryAttemptsRef.current;

      Sentry.addBreadcrumb({
        category: 'twilio',
        level: 'info',
        message: `Refreshing Twilio token after ${source} (attempt ${attempt}/${MAX_TOKEN_RECOVERY_ATTEMPTS})`,
      });

      await new Promise((resolve) => setTimeout(resolve, TOKEN_RECOVERY_BASE_DELAY_MS * attempt));

      // The backoff and the token request together span a second or more, and
      // a client that was still connecting when this started may have come up
      // during them. Rebuilding it now would be the shutdown/create cycle
      // Twilio warns against, performed on a session that just succeeded.
      if (hasInitializedRef.current) {
        return;
      }

      const { data: refreshed } = await refetch();

      const token = refreshed?.token;

      if (!token || hasInitializedRef.current) {
        return;
      }

      // Always rebuild rather than routing through `createClient`. A 'denied'
      // arrives on a client that already initialized, and `createClient` would
      // take its `updateToken` shortcut on that live client — which re-emits no
      // 'initialized', so the success path below never runs, the attempt budget
      // never resets, and a working recovery eventually reports as exhausted.
      // Rebuilding also covers an unchanged token, which leaves the token
      // query's data reference untouched and the effect dormant.
      rebuildClientWithToken(token);
    } finally {
      recoveryInFlightRef.current = false;
    }
  };

  /**
   * Tear the current Twilio client down and build a fresh one from `token`.
   *
   * The replacement emits its own `initialized`, which is what marks a
   * recovery as successful. Listeners are removed before shutdown so the
   * dying client cannot drive another recovery on its way out.
   */
  const rebuildClientWithToken = (token: string) => {
    if (twilioClient) {
      try {
        (
          twilioClientOf(twilioClient) as unknown as { removeAllListeners?: () => void } | undefined
        )?.removeAllListeners?.();
      } catch {}

      try {
        twilioClient.shutdown();
      } catch {}

      setTwilioClient(undefined);
    }

    setupClient(new Client(token));

    markTokenBuilt(token);
  };

  /**
   * Record the token the installed client was built from.
   *
   * Always after the build, never before: tearing a client down clears this,
   * so a value written first would be wiped by the very `setupClient` that
   * replaces the client, and the build effect would then see an unbuilt token
   * and stand up a second client against it.
   */
  const markTokenBuilt = (token: string) => {
    lastBuiltTokenRef.current = token;
  };

  const createClient = async (token: string) => {
    const liveClient = twilioClientOf(twilioClient);

    if (liveClient) {
      try {
        await liveClient.updateToken(token);

        markTokenBuilt(token);

        return;
      } catch (error) {
        console.error('Failed to update Twilio token:', error);

        setupClient(new Client(token));

        markTokenBuilt(token);

        return;
      }
    }

    setupClient(new Client(token));

    markTokenBuilt(token);
  };

  /**
   * Wires the Twilio client's session events here, and its conversation events
   * into the transport-agnostic inbox sync.
   */
  const setupClient = (client: Client) => {
    if (isUnmountedRef.current) {
      try {
        client.shutdown();
      } catch {}

      return;
    }

    teardownClient();

    hasInitializedRef.current = false;

    hasInitFailedRef.current = false;

    clientRef.current = client;

    /**
     * Expose the client only after the SDK has fully initialized. The
     * `connectionStateChanged === 'connected'` event fires when the twilsock
     * connection is up, which is *before* the internal `_conversationsEntity`
     * is populated. Callers that read the atom on `connected` and immediately
     * invoke `peekConversationBySid` race that population and throw
     * "Cannot read properties of undefined (reading 'peekConversation')".
     *
     * `onWithReplay` re-emits the most recent value if the event fired before
     * subscription, avoiding a missed-event window after `new Client(token)`.
     */
    client.onWithReplay('initialized', () => {
      if (isUnmountedRef.current) {
        return;
      }

      const recoveredAfterAttempts = tokenRecoveryAttemptsRef.current;

      const recoverySource = recoverySourceRef.current;

      // A non-zero budget here means this client came up as the result of a
      // recovery rather than a normal first connect. The matching failure only
      // reaches Sentry once recovery is exhausted, so this is the only signal
      // that separates a recovered session from one that never broke.
      if (recoveredAfterAttempts > 0 && recoverySource) {
        Sentry.addBreadcrumb({
          category: 'twilio',
          level: 'info',
          message: `Recovered the Twilio session after ${recoveredAfterAttempts} attempt(s)`,
        });

        trackChatSessionRecovered({
          source: recoverySource,
          attempts: recoveredAfterAttempts,
          user_id: userId ? String(userId) : undefined,
        });
      }

      tokenRecoveryAttemptsRef.current = 0;

      recoverySourceRef.current = undefined;

      hasReportedRecoveryExhaustionRef.current = false;

      hasInitializedRef.current = true;

      hasInitFailedRef.current = false;

      setTwilioClient(new TwilioChatTransport(client, postInquiryMessage));
    });

    client.on('initFailed', ({ error }) => {
      // Every branch below is terminal per the SDK, so the session is only
      // coming back through a rebuild regardless of which one runs. Recording
      // it here rather than per-branch is what lets a resume tell a client the
      // SDK gave up on from one that is merely still connecting.
      hasInitFailedRef.current = true;

      if (isRecoverableTokenError(error)) {
        Sentry.addBreadcrumb({
          category: 'twilio',
          level: 'warning',
          message: `initFailed with a recoverable token error: ${error?.message ?? 'no message'}`,
        });

        recoverChatSession('initFailed').catch(() => {});

        return;
      }

      if (isTransientSocketFailure(error)) {
        Sentry.addBreadcrumb({
          category: 'twilio',
          level: 'warning',
          message: `initFailed on a dropped socket: ${(error as { message: string }).message}`,
        });

        recoverChatSession('networkDrop').catch(() => {});

        return;
      }

      Sentry.captureException(error ?? new Error('Twilio client initFailed without error'), {
        tags: { source: 'twilio:initFailed' },
        extra: {
          errorMessage: error?.message ?? null,
          userId,
          hadError: !!error,
        },
      });
    });

    /**
     * Connection lifecycle, per Twilio Conversations SDK docs.
     *
     * `'denied'` is the SDK's documented "give up" terminus — the JWT was
     * rejected and retry alone will not help. A fresh token does help, so this
     * runs the bounded recovery in `recoverChatSession` and reports only
     * when that budget is spent. Transient socket bounces and reconnects
     * (which surface as `connectionError`) stay breadcrumbs.
     *
     * `'disconnected'` triggers a token refresh as recovery for the common
     * transient case (network blip, mobile background suspend).
     */
    client.on('connectionStateChanged', (state) => {
      Sentry.addBreadcrumb({
        category: 'twilio',
        message: `Connection state changed: ${state}`,
        level: state === 'denied' || state === 'error' ? 'error' : 'info',
      });

      // 'denied' may be followed by a separate 'disconnected' transition. Both
      // paths end in a token refetch and TanStack Query dedups in-flight
      // refetches, so the second call is a safe no-op.
      if (state === 'denied') {
        hasInitFailedRef.current = true;

        recoverChatSession('denied').catch(() => {});

        return;
      }

      if (state === 'disconnected') {
        updateTwilioToken();
      }
    });

    const stopInboxSync = startInboxSync({
      source: twilioInboxEvents(client),
      queryClient,
      userId,
    });

    client.on('tokenAboutToExpire', updateTwilioToken);

    client.on('tokenExpired', updateTwilioToken);

    /**
     * `connectionError` is a low-level diagnostic event, not an actionable
     * one. Per the Twilio Conversations SDK docs, `terminal: false` means
     * the SDK will keep retrying on its own — these errors fire constantly
     * during normal mobile use (background suspend, network handoff, brief
     * twilsock drops) and are silently recovered without any user-visible
     * effect.
     *
     * Capture as a breadcrumb so the diagnostic context attaches to any
     * downstream user-facing capture (peekConversation failures, message
     * send failures, the `'denied'` capture above). The actionable terminus
     * is `connectionStateChanged === 'denied'`, handled above.
     */
    client.on('connectionError', (error: unknown) => {
      const isKnown = isConnectionError(error);

      const data = isKnown
        ? {
            terminal: error.terminal,
            connectionState: client.connectionState,
            httpStatusCode: error.httpStatusCode,
            errorCode: error.errorCode,
            ...(Platform.OS !== 'web' && { appState: AppState.currentState }),
          }
        : { connectionState: client.connectionState };

      Sentry.addBreadcrumb({
        category: 'twilio',
        level: isKnown && error.terminal ? 'warning' : 'info',
        message: `connectionError: ${isKnown ? error.message : String(error)}`,
        data,
      });
    });

    teardownClientRef.current = () => {
      // A client whose init failed never reached the atom, so nothing else
      // holds a reference to shut it down. Without this, every rebuild during
      // an outage strands another one.
      try {
        (client as unknown as { removeAllListeners?: () => void })?.removeAllListeners?.();
      } catch {}

      try {
        client.shutdown();
      } catch {}

      clientRef.current = undefined;

      // Anything still holding this client through the atom would be calling
      // methods on a shut-down object, so the reference has to go with it.
      // Clearing the token alongside is what lets the build effect run again:
      // it skips a token it has already built from, which after a teardown
      // would leave the session dead with no way to ask for another.
      lastBuiltTokenRef.current = undefined;

      setTwilioClient((current) => (twilioClientOf(current) === client ? undefined : current));

      stopInboxSync();
    };
  };

  return { createClient };
};

/**
 * Twilio Conversations SDK `ConnectionError` shape.
 *
 * The public SDK type only declares `terminal` and `message`, but the runtime
 * payload also includes optional `httpStatusCode` and `errorCode` (per the
 * `connectionError` event JSDoc in `lib.d.ts`). We type both here for
 * diagnostic context.
 */
type ConnectionError = {
  terminal: boolean;
  message: string;
  httpStatusCode?: number;
  errorCode?: number;
};

export function isConnectionError(error: unknown): error is ConnectionError {
  return typeof error === 'object' && error !== null && 'terminal' in error;
}
